import { useEffect, useState } from 'react'
import { Alert, Button, Group, Text } from '@mantine/core'
import { IconFingerprint } from '@tabler/icons-react'
import { useNavigate } from 'react-router'
import { useAuthStore } from '../../stores/authStore'
import { authClient } from '../../api/authClient'

const SNOOZE_MS = 30 * 24 * 60 * 60 * 1000

// Per user, so two admins sharing a browser are snoozed independently. localStorage only:
// passkeys are per-device, so declining on one machine shouldn't stop the suggestion on another.
function snoozeKey(userId: string): string {
  return `passkeySetupSnoozedUntil:${userId}`
}

function isSnoozed(userId: string): boolean {
  try {
    const until = Number(localStorage.getItem(snoozeKey(userId)))
    return Number.isFinite(until) && until > Date.now()
  } catch {
    return false
  }
}

function snooze(userId: string): void {
  try {
    localStorage.setItem(snoozeKey(userId), String(Date.now() + SNOOZE_MS))
  } catch {
    // Storage unavailable (private mode, blocked): the banner just reappears next visit.
  }
}

/**
 * Reads the `amr` claim from the access token payload. Display logic only — the token is not
 * verified here and nothing security-relevant depends on the result.
 */
function sessionAmr(accessToken: string | null): string[] {
  if (!accessToken) return []
  try {
    const payload = accessToken.split('.')[1]
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
    const amr = JSON.parse(json).amr
    return Array.isArray(amr) ? amr : []
  } catch {
    return []
  }
}

/**
 * Launcher banner suggesting passkey setup after a password sign-in. Shown only when all hold:
 * the user is an Admin (the only role that can reach Settings today — non-admins get this once
 * consuming apps build their own enrollment UI), this session started with a password, the
 * browser supports passkeys, passkeys and enrollment are enabled for auth-admin, the user has
 * no passkeys yet, and they haven't snoozed it in this browser within 30 days.
 */
export function PasskeySetupBanner() {
  const user = useAuthStore((s) => s.user)
  const accessToken = useAuthStore((s) => s.accessToken)
  const navigate = useNavigate()
  const [visible, setVisible] = useState(false)

  const userId = user?.userId
  const eligibleSession =
    !!userId && user?.role === 'Admin' && sessionAmr(accessToken).includes('pwd')

  useEffect(() => {
    if (!eligibleSession || !userId || isSnoozed(userId) || !authClient.passkeys.isSupported()) {
      return
    }

    let cancelled = false
    async function check() {
      try {
        const config = await authClient.passkeys.getConfig()
        if (cancelled || !config.enabled || !config.enrollmentEnabled) return
        const passkeys = await authClient.passkeys.list()
        if (!cancelled && passkeys.length === 0) setVisible(true)
      } catch {
        // A suggestion, not a feature: any failure just means no banner.
      }
    }
    check()
    return () => {
      cancelled = true
    }
  }, [eligibleSession, userId])

  if (!visible || !userId) return null

  return (
    <Alert icon={<IconFingerprint size={18} />} color="blue" variant="light">
      <Group justify="space-between" align="center" wrap="wrap" gap="sm">
        <Text size="sm">Sign in faster next time — set up a passkey.</Text>
        <Group gap="xs">
          <Button size="xs" onClick={() => navigate('/settings?addPasskey=1')}>
            Set up passkey
          </Button>
          <Button
            size="xs"
            variant="subtle"
            onClick={() => {
              snooze(userId)
              setVisible(false)
            }}
          >
            Not now
          </Button>
        </Group>
      </Group>
    </Alert>
  )
}
